import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { useDeleteDocumentType } from "./use-document-types";
import type { TosDocumentType } from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

interface Props {
  readonly target: TosDocumentType | null;
  readonly onOpenChange: (open: boolean) => void;
}

export function DeleteDocumentTypeDialog({ target, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const remove = useDeleteDocumentType();

  useEffect(() => {
    if (target) setError(null);
  }, [target]);

  const handleSubmit = async () => {
    if (!target || remove.isPending) return;
    setError(null);
    try {
      await remove.mutateAsync(target.id);
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <Dialog open={target != null} onOpenChange={(next) => !remove.isPending && onOpenChange(next)}>
      <DialogContent data-testid="compliance-delete-document-type-dialog">
        <DialogHeader>
          <DialogTitle>{t("compliance.documentTypes.delete.title")}</DialogTitle>
          <DialogDescription>
            {t("compliance.documentTypes.delete.description", {
              key: target?.key ?? "",
            })}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p
            data-testid="compliance-delete-document-type-error"
            className="text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={remove.isPending}>
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            variant="destructive"
            data-testid="compliance-delete-document-type-submit"
            onClick={handleSubmit}
            disabled={remove.isPending}
          >
            {remove.isPending ? t("compliance.actions.deleting") : t("compliance.actions.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
